import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { DxcSceneName, DxcSceneTone } from '../dxc.types';

/**
 * La escena del desenlace: el lienzo ilustrado de un diálogo de resultado.
 *
 * Es una familia aparte de `dxc-state-icon` y la frontera es exacta:
 *
 * · Un **icono de estado** describe una condición que sigue siendo cierta —«no
 *   hay GPS», «no hay avisos»—. Vive en un cuadrado de 96, late en bucle y
 *   titula un vacío.
 * · Una **escena** describe un suceso que acaba de concluir —«la carga quedó
 *   entregada», «el PAC rechazó el timbre»—. Vive en una banda apaisada de
 *   320×140, se reproduce UNA vez y se queda en su desenlace.
 *
 * Las dos comparten la línea de suelo, la gramática de trazos (`ln` `sf` `ac`
 * `acf`) y el criterio de dibujo del dominio (R8): lo que se dibuja es el
 * contenedor, la pluma y la carta porte, no una marca de verificación genérica.
 *
 * **El fotograma en reposo es el significado.** La animación cuenta cómo se
 * llegó ahí; nunca es lo que lo hace verdadero. Con la pestaña en segundo plano
 * el reloj del documento no avanza y la animación se queda en su retardo, así
 * que una escena que necesitara animarse para decir «el contenedor está en el
 * suelo» diría lo contrario justo cuando nadie la mira. Es la misma lección que
 * costó el punto verde del stepper vertical.
 *
 * Dos escenas van en bucle a propósito —`decision` y `en-ruta`— porque su
 * condición sigue siendo cierta mientras se ven: una pregunta está esperando
 * respuesta y un tránsito sigue en curso.
 */
@Component({
    standalone: true,
    selector: 'dxc-scene',
    imports: [CommonModule],
    templateUrl: './dxc-scene.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcSceneComponent {

    @Input() name: DxcSceneName = 'entrega';

    /**
     * Se omite para tomar el tono natural de la escena. Solo se nombra cuando
     * el dominio contradice al dibujo — un aviso que en realidad bloquea.
     */
    @Input() tone?: DxcSceneTone;

    /** Vacío cuando el título contiguo ya dice lo mismo: si no, se lee dos veces. */
    @Input() ariaLabel = '';

    /** Tono natural de cada escena. Sale del eje semántico de §5, no de una gama propia. */
    static readonly DEFAULT_TONE: Record<DxcSceneName, DxcSceneTone> = {
        'entrega': 'success',
        'carga-caida': 'danger',
        'retencion': 'warning',
        'decision': 'neutral',
        'en-ruta': 'info',
        'timbrado': 'success',
        'sello-rechazado': 'danger',
    };

    get hostClasses() {
        const tone = this.tone ?? DxcSceneComponent.DEFAULT_TONE[this.name] ?? 'neutral';
        return `dxc-scene dxc-scene--${tone}`;
    }
}
